import React from 'react';
import { useMutation } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { formatTimestamp } from '@ssg/common/Helpers/Helpers';
import { loader } from 'graphql.macro';
import { useTranslation } from 'react-i18next';
import { GetJobs, GetJobsVariables, GetJobs_jobs, JobSource, UpdateJob, UpdateJobVariables } from '../../GraphQL';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { useFlag } from '@unleash/proxy-client-react';
import Button from '@ssg/common/Components/Button';
import Table from '@ssg/common/Components/Table';
import TextButton from '@ssg/common/Components/TextButton';

const GET_JOBS = loader('src/GraphQL/Jobs/GetJobs.gql');
const UPDATE_JOB = loader('src/GraphQL/Jobs/UpdateJob.gql');

export const isJobOverdue = (job: GetJobs_jobs): boolean => {
	const deadlineDate = new Date(job.deadline);
	const now = new Date();
	return now > deadlineDate;
};

interface Props {
	jobs: GetJobs_jobs[];
	queryProps: GetJobsVariables;
	loading?: boolean;
	noDataText: string;
}

const JobsTable: React.FC<Props> = ({ jobs, queryProps, loading, noDataText }) => {
	const { t } = useTranslation();

	const myTasksCloseFix = useFlag(FeatureFlagEnums.MY_TASKS_CLOSE_FIX);

	const [updateJob] = useMutation<UpdateJob, UpdateJobVariables>(UPDATE_JOB);

	return (
		<Table
			loading={loading}
			data={jobs}
			keySelector={j => j.id}
			noDataFoundText={noDataText}
			columns={[
				{
					label: 'common.description',
					selectFn: j => j.description,
					sortFn: (a, b) => a.description.localeCompare(b.description),
				},
				{
					label: 'common.deadline',
					selectFn: j => {
						const deadlineDate = new Date(j.deadline);
						const isOverdue = isJobOverdue(j);
						return <span className={isOverdue ? 'text-red' : undefined}>{formatTimestamp(deadlineDate)}</span>;
					},
					sortFn: (a, b) => a.deadline.localeCompare(b.deadline),
				},
				{
					label: 'jobs.assignedTo',
					selectFn: j => j.assignedTo?.name ?? '',
					sortFn: (a, b) => (a.assignedTo?.name ?? '').localeCompare(b.assignedTo?.name ?? ''),
				},
				{
					label: 'common.location',
					selectFn: j => j.location?.name ?? '',
					sortFn: (a, b) => (a.location?.name ?? '').localeCompare(b.location?.name ?? ''),
				},
				{
					label: 'common.case',
					selectFn: j => (
						<div>
							{j.case?.erpNo ?? ''}
							{j.movable !== null && (
								<>
									{j.case?.erpNo && <br />}
									{`${j.movable.description}, ${j.movable.status}, ${j.movable.placement.name}`}
								</>
							)}
						</div>
					),
					sortFn: (a, b) => (a.case?.erpNo ?? '').localeCompare(b.case?.erpNo ?? ''),
				},
				{
					label: 'Scalepoint link',
					selectFn: j =>
						j.source === JobSource.SCALEPOINT && j.case?.scalePointUrl ? (
							<TextButton link text="Gå til ScalePoint" onClick={() => window.open(j?.case?.scalePointUrl ?? '', '_blank')} />
						) : (
							'-'
						),
					hideColumn: !myTasksCloseFix,
				},
				{
					label: 'common.complete',
					selectFn: j => (
						<Button
							primary
							className="my-1"
							icon={faCheck}
							onClick={async () => {
								if (window.confirm(t('movable.confirmCompleteJobMessage'))) {
									await updateJob({
										variables: {
											id: j.id,
											completed: true,
											assignedTo: j.assignedTo?.id,
										},
										update: (cache, { data }): void => {
											if (typeof data === 'undefined' || data === null) {
												return;
											}
											// Update cache for personal only query
											const cachedRequestPersonalOnly = cache.readQuery<GetJobs, GetJobsVariables>({
												query: GET_JOBS,
												variables: queryProps,
											});
											if (cachedRequestPersonalOnly !== null) {
												cache.writeQuery<GetJobs, GetJobsVariables>({
													query: GET_JOBS,
													variables: queryProps,
													data: {
														jobs: cachedRequestPersonalOnly.jobs.filter(cj => cj.id !== j.id),
													},
												});
											}
											// Update cache for "all" query
											const cachedRequestAll = cache.readQuery<GetJobs, GetJobsVariables>({
												query: GET_JOBS,
												variables: queryProps,
											});
											if (cachedRequestAll !== null && cachedRequestAll.jobs !== null) {
												cache.writeQuery({
													query: GET_JOBS,
													variables: queryProps,
													data: {
														jobs: cachedRequestAll.jobs.filter(cj => cj.id !== data.updateJob?.id),
													},
												});
											}
										},
									});
								}
							}}
							disabled={j.source === JobSource.SCALEPOINT}
						/>
					),
				},
			]}
		/>
	);
};

export default JobsTable;

